import type Anthropic from '@anthropic-ai/sdk';
import { useAppStore } from '../stores/useAppStore';
import { useUsage } from '../stores/useUsage';
import { getTemplate } from '../templates/registry';
import { withRetry } from './retry';
import { makeAttemptSignal } from './abortHelpers';
import { generateText } from './anthropic';
import { llmMessages } from '../lib/syncClient';
import {
  TOOLS_FOR_API,
  executeRead,
  executeWrite,
  getToolKind,
  type ToolDispatchContext,
} from './chatTools';
import type {
  ChatAssistantBlock,
  ChatMessage,
  ChatToolResultBlock,
  ChatToolUseBlock,
  ChatUserBlock,
} from '../types';

export type PendingWrite = {
  tool_use_id: string;
  name: string;
  input: unknown;
};

export type TurnStep =
  | { kind: 'done'; messages: ChatMessage[] }
  | {
      kind: 'awaiting_approval';
      messages: ChatMessage[];
      pending: PendingWrite[];
    }
  | { kind: 'error'; messages: ChatMessage[]; error: string };

export type WriteResolution = {
  tool_use_id: string;
  approved: boolean;
};

function buildChatSystem(universeId: string) {
  const state = useAppStore.getState();
  const universe = state.universes[universeId];
  if (!universe) throw new Error('Universe not found');

  const metaLines = [
    `# Universe: ${universe.name}`,
    universe.description ? `Description: ${universe.description}` : null,
    `Default protagonists: ${universe.defaultProtagonists}`,
  ].filter(Boolean) as string[];

  const blocks = [
    { type: 'text' as const, text: metaLines.join('\n') },
    {
      type: 'text' as const,
      text: `## Story bible — combined effective text (default + override + extension)

This is the exact text injected into every stage of story generation. To see how it breaks down (shipped default vs. universe override vs. extension), call read_prompt({"templateId":"story_bible"}).

${getTemplate('story_bible', universeId)}`,
      cache_control: { type: 'ephemeral' as const },
    },
    {
      type: 'text' as const,
      text: `## Voice config — combined effective text (default + override + extension)

To see the breakdown, call read_prompt({"templateId":"voice_config"}).

${getTemplate('voice_config', universeId)}`,
      cache_control: { type: 'ephemeral' as const },
    },
  ];

  const canon = universe.canon?.trim();
  if (canon) {
    blocks.push({
      type: 'text' as const,
      text: `## Canon\n\n${canon}`,
      cache_control: { type: 'ephemeral' as const },
    });
  }

  blocks.push({
    type: 'text' as const,
    text: `## Your role

You are helping the user curate the "${universe.name}" universe — its canon, prompts, voice, and identity. The audience and register of the stories are whatever this universe's story bible declares; if the bible is empty on those points, ask before generating.

Use read tools to fetch artifacts on demand (stories, prompts, canon details). Use write tools to propose edits. The user reviews each write tool call and either applies or rejects it — edits do NOT take effect until they approve.

When the user is brainstorming new stories or describing a plot concept / multi-story arc, use propose_slugs to offer a batch of concrete tappable options (instead of writing them out as prose in your reply). The slugs land in this universe's slug pool and render as cards inline; the user taps Auto or Edit on whichever they like. Pass a "note" with the framing — it becomes batch guidance threaded into outline + prose generation.

Style:
- Be concise. The user is often tired and in a rush.
- Don't restate artifacts back to the user — they can read what you fetched.
- Quote sparingly. Brief summaries are better than reprints.
- When proposing an edit, give a one-sentence reason before the tool call.
- When proposing slugs, give a one-sentence framing before the tool call (don't list the titles in prose — the cards do that).
- If you call a write tool that the user rejects, accept it and offer alternatives — don't argue.`,
  });

  return blocks;
}

function toApiMessage(m: ChatMessage): Anthropic.MessageParam {
  if (m.role === 'user') {
    const content = m.content.map((b) => {
      if (b.type === 'text') return { type: 'text' as const, text: b.text };
      return {
        type: 'tool_result' as const,
        tool_use_id: b.tool_use_id,
        content: b.content,
        ...(b.is_error ? { is_error: true } : {}),
      };
    });
    return { role: 'user', content };
  }
  const content = m.content.map((b) => {
    if (b.type === 'text') return { type: 'text' as const, text: b.text };
    return {
      type: 'tool_use' as const,
      id: b.id,
      name: b.name,
      input: b.input as Record<string, unknown>,
    };
  });
  return { role: 'assistant', content };
}

async function callModel(
  messages: ChatMessage[],
  ctx: ToolDispatchContext,
  signal?: AbortSignal
): Promise<ChatMessage> {
  const state = useAppStore.getState();

  return withRetry(
    async () => {
      const ctl = makeAttemptSignal(signal, 90_000, 'chat');
      try {
        const resp = await llmMessages<Anthropic.Message>(
          {
            model: state.settings.model,
            max_tokens: 4096,
            system: buildChatSystem(ctx.universeId),
            tools: TOOLS_FOR_API,
            messages: messages.map(toApiMessage),
          },
          ctl.signal
        );
        try {
          useUsage.getState().recordAnthropic(state.settings.model, resp.usage);
        } catch (e) {
          console.warn('usage record failed (chat)', e);
        }
        const content: ChatAssistantBlock[] = resp.content.map((b) => {
          if (b.type === 'text') return { type: 'text', text: b.text };
          if (b.type === 'tool_use')
            return { type: 'tool_use', id: b.id, name: b.name, input: b.input };
          return { type: 'text', text: '' };
        });
        return { role: 'assistant', content, at: Date.now() };
      } catch (e) {
        if (ctl.isOurAbort()) {
          throw new DOMException(
            (e as Error)?.message ?? 'chat timed out',
            'TimeoutError'
          );
        }
        throw e;
      } finally {
        ctl.cleanup();
      }
    },
    { signal, label: 'chat' }
  );
}

async function loop(
  messages: ChatMessage[],
  ctx: ToolDispatchContext,
  signal?: AbortSignal
): Promise<TurnStep> {
  let current = messages;
  while (true) {
    const assistant = await callModel(current, ctx, signal);
    current = [...current, assistant];

    const toolUses = assistant.content.filter(
      (b): b is ChatToolUseBlock => b.type === 'tool_use'
    );

    if (toolUses.length === 0) {
      return { kind: 'done', messages: current };
    }

    const writes = toolUses.filter((tu) => getToolKind(tu.name) === 'write');
    if (writes.length > 0) {
      return {
        kind: 'awaiting_approval',
        messages: current,
        pending: writes.map((tu) => ({
          tool_use_id: tu.id,
          name: tu.name,
          input: tu.input,
        })),
      };
    }

    const results: ChatToolResultBlock[] = [];
    for (const tu of toolUses) {
      const r = await executeRead(tu.name, tu.input, ctx);
      results.push({
        type: 'tool_result',
        tool_use_id: tu.id,
        content: r.content,
        ...(r.is_error ? { is_error: true } : {}),
        approval: 'auto',
      });
    }
    current = [
      ...current,
      { role: 'user', content: results, at: Date.now() },
    ];
  }
}

export async function startChatTurn(args: {
  messages: ChatMessage[];
  newUserText: string;
  ctx: ToolDispatchContext;
  signal?: AbortSignal;
}): Promise<TurnStep> {
  const userMsg: ChatMessage = {
    role: 'user',
    content: [{ type: 'text', text: args.newUserText }],
    at: Date.now(),
  };
  const next = [...args.messages, userMsg];
  try {
    return await loop(next, args.ctx, args.signal);
  } catch (e) {
    return {
      kind: 'error',
      messages: next,
      error: (e as Error).message ?? 'Unknown error',
    };
  }
}

export async function resumeChatTurn(args: {
  messages: ChatMessage[];
  resolutions: WriteResolution[];
  ctx: ToolDispatchContext;
  signal?: AbortSignal;
}): Promise<TurnStep> {
  const last = args.messages[args.messages.length - 1];
  if (!last || last.role !== 'assistant') {
    return {
      kind: 'error',
      messages: args.messages,
      error: 'Cannot resume: last message is not an assistant turn.',
    };
  }
  const toolUses = last.content.filter(
    (b): b is ChatToolUseBlock => b.type === 'tool_use'
  );
  if (toolUses.length === 0) {
    return { kind: 'done', messages: args.messages };
  }

  const results: ChatToolResultBlock[] = [];
  for (const tu of toolUses) {
    const kind = getToolKind(tu.name);
    if (kind === 'read') {
      const r = await executeRead(tu.name, tu.input, args.ctx);
      results.push({
        type: 'tool_result',
        tool_use_id: tu.id,
        content: r.content,
        ...(r.is_error ? { is_error: true } : {}),
        approval: 'auto',
      });
    } else if (kind === 'write') {
      const resolution = args.resolutions.find(
        (x) => x.tool_use_id === tu.id
      );
      if (!resolution) {
        return {
          kind: 'error',
          messages: args.messages,
          error: `Missing resolution for tool call ${tu.id}.`,
        };
      }
      if (resolution.approved) {
        const r = await executeWrite(tu.name, tu.input, args.ctx);
        results.push({
          type: 'tool_result',
          tool_use_id: tu.id,
          content: r.content,
          ...(r.is_error ? { is_error: true } : {}),
          approval: 'applied',
        });
      } else {
        results.push({
          type: 'tool_result',
          tool_use_id: tu.id,
          content: 'User declined to apply this change.',
          approval: 'rejected',
        });
      }
    } else {
      results.push({
        type: 'tool_result',
        tool_use_id: tu.id,
        content: `Unknown tool: ${tu.name}`,
        is_error: true,
      });
    }
  }

  const next: ChatMessage[] = [
    ...args.messages,
    { role: 'user', content: results as ChatUserBlock[], at: Date.now() },
  ];
  try {
    return await loop(next, args.ctx, args.signal);
  } catch (e) {
    return {
      kind: 'error',
      messages: next,
      error: (e as Error).message ?? 'Unknown error',
    };
  }
}

export function findPendingWrites(messages: ChatMessage[]): PendingWrite[] {
  const last = messages[messages.length - 1];
  if (!last || last.role !== 'assistant') return [];
  return last.content
    .filter((b): b is ChatToolUseBlock => b.type === 'tool_use')
    .filter((tu) => getToolKind(tu.name) === 'write')
    .map((tu) => ({ tool_use_id: tu.id, name: tu.name, input: tu.input }));
}

const TITLE_SYSTEM = `Generate a concise topic title for the following chat snippet.
Rules: 3 to 6 words, title case, no quotes, no trailing punctuation, no leading "Topic:" or similar prefix. Return ONLY the title text on a single line.`;

export async function summarizeChatTitle(
  messages: ChatMessage[],
  signal?: AbortSignal
): Promise<string | undefined> {
  const lines: string[] = [];
  for (const m of messages.slice(0, 3)) {
    const text = m.content
      .filter((b): b is { type: 'text'; text: string } => b.type === 'text')
      .map((b) => b.text)
      .join(' ')
      .trim();
    if (!text) continue;
    const role = m.role === 'user' ? 'User' : 'Assistant';
    lines.push(`${role}: ${text.slice(0, 600)}`);
  }
  if (lines.length === 0) return undefined;

  try {
    const raw = await generateText({
      model: 'claude-haiku-4-5',
      systemBlocks: [{ type: 'text', text: TITLE_SYSTEM }],
      userText: lines.join('\n\n'),
      maxTokens: 40,
      signal,
      timeoutMs: 30_000,
      label: 'chat-summary',
    });
    const cleaned = raw
      .trim()
      .split('\n')[0]
      .replace(/^["'`]+|["'`]+$/g, '')
      .replace(/\s+/g, ' ')
      .replace(/[.!?]+$/, '')
      .trim();
    if (!cleaned) return undefined;
    return cleaned.slice(0, 80);
  } catch (e) {
    console.warn('chat title summary failed', e);
    return undefined;
  }
}
